import React, { useState, useEffect } from 'react';
import '../App.css';

interface SystemHoliday {
  id: number;
  holiday_name: string;
  holiday_date: string; // ISO date string
  applies_to: string; // 'all', 'country', 'practice_type'
  country_iso2: string | null;
  practice_type: string | null;
  is_recurring: boolean;
  year: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
  created_by: number | null;
}

interface CountryCode {
  id: number;
  country_name: string;
  iso2: string;
  dial_code: string;
  min_digits: number;
  max_digits: number;
  is_active: boolean;
}

interface BulkSummary {
  total_holidays: number;
  dates_with_appointments: number;
  affected_appointments_count: number;
  dates_with_appointments_list: Array<{
    holiday_name: string;
    holiday_date: string;
    appointment_count: number;
    appointments: Array<{
      id: number;
      patient_id: number;
      appointment_date: string;
      status: string;
    }>;
  }>;
  holidays_to_import: Array<{
    holiday_name: string;
    holiday_date: string;
    has_appointments: boolean;
  }>;
  holidays_skipped: Array<{
    holiday_name: string;
    holiday_date: string;
    reason: string;
  }>;
}

interface SystemHolidaysProps {
  apiUrl: (path: string) => string;
  getAuthHeaders: (payload?: any) => Record<string, string>;
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const SystemHolidays: React.FC<SystemHolidaysProps> = ({ apiUrl, getAuthHeaders, safeFetch = fetch }) => {
  const [holidays, setHolidays] = useState<SystemHoliday[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [showYearImportModal, setShowYearImportModal] = useState(false);
  const [showPreCheckModal, setShowPreCheckModal] = useState(false);
  const [preCheckSummary, setPreCheckSummary] = useState<BulkSummary | null>(null);
  const [selectedHoliday, setSelectedHoliday] = useState<SystemHoliday | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteHolidayId, setDeleteHolidayId] = useState<number | null>(null);

  // Form states
  const [holidayForm, setHolidayForm] = useState<{
    holiday_name: string;
    holiday_date: string;
    applies_to: string;
    country_iso2: string;
    practice_type: string;
    is_recurring: boolean;
    year: number | null;
    is_active: boolean;
  }>({
    holiday_name: '',
    holiday_date: '',
    applies_to: 'all',
    country_iso2: '',
    practice_type: '',
    is_recurring: false,
    year: new Date().getFullYear(),
    is_active: true
  });

  const [bulkHolidaysText, setBulkHolidaysText] = useState('');
  const [autoCancelAppointments, setAutoCancelAppointments] = useState(false);
  const [importOnlyWithoutAppointments, setImportOnlyWithoutAppointments] = useState(false);

  // Fetch holidays
  const fetchHolidays = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await safeFetch(
        apiUrl(`/api/v1/admin/system-holidays?year=${selectedYear}`),
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch holidays' }));
        setError(errorData.detail || 'Failed to fetch holidays');
      }
    } catch (e) {
      console.error('Failed to fetch holidays', e);
      setError('Failed to fetch holidays');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch country codes
  const fetchCountryCodes = async () => {
    try {
      const response = await safeFetch(apiUrl('/api/v1/country-codes'), {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCountryCodes(data);
      }
    } catch (e) {
      console.error('Failed to fetch country codes', e);
    }
  };

  useEffect(() => {
    fetchHolidays();
    fetchCountryCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Create holiday
  const handleCreateHoliday = async () => {
    if (!holidayForm.holiday_name || !holidayForm.holiday_date) {
      setError('Please fill in holiday name and date');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const payload = {
        holiday_name: holidayForm.holiday_name,
        holiday_date: holidayForm.holiday_date,
        applies_to: holidayForm.applies_to,
        country_iso2: holidayForm.applies_to === 'country' ? holidayForm.country_iso2 || null : null,
        practice_type: holidayForm.applies_to === 'practice_type' ? holidayForm.practice_type || null : null,
        is_recurring: holidayForm.is_recurring,
        year: holidayForm.year || null,
        is_active: holidayForm.is_active
      };

      const response = await safeFetch(
        apiUrl(`/api/v1/admin/system-holidays?auto_cancel_appointments=${autoCancelAppointments}`),
        {
          method: 'POST',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        setSuccess('Holiday created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create holiday' }));
        setError(errorData.detail || 'Failed to create holiday');
      }
    } catch (e) {
      console.error('Failed to create holiday', e);
      setError('Failed to create holiday');
    } finally {
      setIsLoading(false);
    }
  };

  // Update holiday
  const handleUpdateHoliday = async () => {
    if (!selectedHoliday) return;
    if (!holidayForm.holiday_name || !holidayForm.holiday_date) {
      setError('Please fill in holiday name and date');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const payload: any = {
        holiday_name: holidayForm.holiday_name,
        holiday_date: holidayForm.holiday_date,
        applies_to: holidayForm.applies_to,
        is_recurring: holidayForm.is_recurring,
        year: holidayForm.year || null,
        is_active: holidayForm.is_active
      };

      if (holidayForm.applies_to === 'country') {
        payload.country_iso2 = holidayForm.country_iso2 || null;
      } else {
        payload.country_iso2 = null;
      }

      if (holidayForm.applies_to === 'practice_type') {
        payload.practice_type = holidayForm.practice_type || null;
      } else {
        payload.practice_type = null;
      }

      const response = await safeFetch(
        apiUrl(`/api/v1/admin/system-holidays/${selectedHoliday.id}?auto_cancel_appointments=${autoCancelAppointments}`),
        {
          method: 'PUT',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        setSuccess('Holiday updated successfully');
        setShowEditModal(false);
        setSelectedHoliday(null);
        resetForm();
        fetchHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to update holiday' }));
        setError(errorData.detail || 'Failed to update holiday');
      }
    } catch (e) {
      console.error('Failed to update holiday', e);
      setError('Failed to update holiday');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete holiday
  const handleDeleteHoliday = (id: number) => {
    setDeleteHolidayId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteHoliday = async () => {
    if (!deleteHolidayId) return;

    setIsLoading(true);
    setError('');
    setShowDeleteConfirm(false);
    try {
      const response = await safeFetch(
        apiUrl(`/api/v1/admin/system-holidays/${deleteHolidayId}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        setSuccess('Holiday deleted successfully');
        fetchHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete holiday' }));
        setError(errorData.detail || 'Failed to delete holiday');
      }
    } catch (e) {
      console.error('Failed to delete holiday', e);
      setError('Failed to delete holiday');
    } finally {
      setIsLoading(false);
      setDeleteHolidayId(null);
    }
  };

  // Pre-check bulk import
  const handlePreCheckBulk = async () => {
    const holidaysList = parseBulkHolidaysText();
    if (holidaysList.length === 0) {
      setError('Please enter at least one holiday');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const payload = {
        holidays: holidaysList,
        auto_cancel_appointments: autoCancelAppointments,
        import_only_without_appointments: importOnlyWithoutAppointments
      };

      const response = await safeFetch(
        apiUrl('/api/v1/admin/system-holidays/bulk/pre-check'),
        {
          method: 'POST',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const summary = await response.json();
        setPreCheckSummary(summary);
        setShowPreCheckModal(true);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to pre-check bulk import' }));
        setError(errorData.detail || 'Failed to pre-check bulk import');
      }
    } catch (e) {
      console.error('Failed to pre-check bulk import', e);
      setError('Failed to pre-check bulk import');
    } finally {
      setIsLoading(false);
    }
  };

  // Bulk import holidays
  const handleBulkImport = async () => {
    const holidaysList = parseBulkHolidaysText();
    if (holidaysList.length === 0) {
      setError('Please enter at least one holiday');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const payload = {
        holidays: holidaysList,
        auto_cancel_appointments: autoCancelAppointments,
        import_only_without_appointments: importOnlyWithoutAppointments
      };

      const response = await safeFetch(
        apiUrl('/api/v1/admin/system-holidays/bulk'),
        {
          method: 'POST',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSuccess(`Successfully created ${result.created_holidays.length} holiday(s)`);
        setShowBulkImportModal(false);
        setShowPreCheckModal(false);
        setBulkHolidaysText('');
        setPreCheckSummary(null);
        fetchHolidays();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to import holidays' }));
        setError(errorData.detail || 'Failed to import holidays');
      }
    } catch (e) {
      console.error('Failed to bulk import', e);
      setError('Failed to bulk import holidays');
    } finally {
      setIsLoading(false);
    }
  };

  // Import year holidays
  const handleImportYear = async () => {
    setIsLoading(true);
    setError('');
    try {
      const payload = {
        year: selectedYear,
        auto_cancel_appointments: autoCancelAppointments,
        import_only_without_appointments: importOnlyWithoutAppointments
      };

      const response = await safeFetch(
        apiUrl('/api/v1/admin/system-holidays/import-year'),
        {
          method: 'POST',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSuccess(`Successfully imported ${result.created_holidays.length} holiday(s) for ${selectedYear}`);
        setShowYearImportModal(false);
        fetchHolidays();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to import year holidays' }));
        setError(errorData.detail || 'Failed to import year holidays');
      }
    } catch (e) {
      console.error('Failed to import year holidays', e);
      setError('Failed to import year holidays');
    } finally {
      setIsLoading(false);
    }
  };

  // Pre-check year import
  const handlePreCheckYear = async () => {
    setIsLoading(true);
    setError('');
    try {
      const payload = {
        year: selectedYear,
        auto_cancel_appointments: autoCancelAppointments,
        import_only_without_appointments: importOnlyWithoutAppointments
      };

      const response = await safeFetch(
        apiUrl('/api/v1/admin/system-holidays/import-year/pre-check'),
        {
          method: 'POST',
          headers: getAuthHeaders(payload),
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const summary = await response.json();
        setPreCheckSummary(summary);
        setShowPreCheckModal(true);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to pre-check year import' }));
        setError(errorData.detail || 'Failed to pre-check year import');
      }
    } catch (e) {
      console.error('Failed to pre-check year import', e);
      setError('Failed to pre-check year import');
    } finally {
      setIsLoading(false);
    }
  };

  // Parse bulk holidays text (format: "Holiday Name,YYYY-MM-DD" one per line)
  const parseBulkHolidaysText = (): Array<{
    holiday_name: string;
    holiday_date: string;
    applies_to: string;
    is_recurring: boolean;
    year: number | null;
    is_active: boolean;
  }> => {
    const lines = bulkHolidaysText.split('\n').filter(line => line.trim());
    return lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      if (parts.length < 2) {
        throw new Error(`Invalid format: ${line}. Expected: "Holiday Name,YYYY-MM-DD"`);
      }
      return {
        holiday_name: parts[0],
        holiday_date: parts[1],
        applies_to: 'all',
        is_recurring: false,
        year: selectedYear,
        is_active: true
      };
    });
  };

  // Reset form
  const resetForm = () => {
    setHolidayForm({
      holiday_name: '',
      holiday_date: '',
      applies_to: 'all',
      country_iso2: '',
      practice_type: '',
      is_recurring: false,
      year: selectedYear,
      is_active: true
    });
    setAutoCancelAppointments(false);
    setImportOnlyWithoutAppointments(false);
  };

  // Open edit modal
  const openEditModal = (holiday: SystemHoliday) => {
    setSelectedHoliday(holiday);
    setHolidayForm({
      holiday_name: holiday.holiday_name,
      holiday_date: holiday.holiday_date,
      applies_to: holiday.applies_to,
      country_iso2: holiday.country_iso2 || '',
      practice_type: holiday.practice_type || '',
      is_recurring: holiday.is_recurring,
      year: holiday.year || selectedYear,
      is_active: holiday.is_active
    });
    setShowEditModal(true);
  };

  // Format date for display
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="admin-management">
      <div className="section-header">
        <h2>System Holidays Management</h2>
        <div className="header-actions">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ marginRight: '10px', padding: '8px' }}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            style={{ marginRight: '10px' }}
          >
            + Add Holiday
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setShowYearImportModal(true);
            }}
            style={{ marginRight: '10px' }}
          >
            Import Year Holidays
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setBulkHolidaysText('');
              setShowBulkImportModal(true);
            }}
          >
            Bulk Import
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {success}
        </div>
      )}

      {isLoading && holidays.length === 0 ? (
        <div className="loading">Loading holidays...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Holiday Name</th>
                <th>Date</th>
                <th>Applies To</th>
                <th>Year</th>
                <th>Recurring</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    No holidays found for {selectedYear}
                  </td>
                </tr>
              ) : (
                holidays.map(holiday => (
                  <tr key={holiday.id}>
                    <td>{holiday.holiday_name}</td>
                    <td>{formatDate(holiday.holiday_date)}</td>
                    <td>
                      {holiday.applies_to === 'all' ? 'All' :
                       holiday.applies_to === 'country' ? (() => {
                         const country = countryCodes.find(c => c.iso2 === holiday.country_iso2);
                         return country ? `${country.country_name} (${holiday.country_iso2})` : `Country: ${holiday.country_iso2 || 'N/A'}`;
                       })() :
                       `Practice Type: ${holiday.practice_type || 'N/A'}`}
                    </td>
                    <td>{holiday.year || 'All Years'}</td>
                    <td>{holiday.is_recurring ? 'Yes' : 'No'}</td>
                    <td>
                      <span className={`badge ${holiday.is_active ? 'badge-success' : 'badge-secondary'}`}>
                        {holiday.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openEditModal(holiday)}
                        style={{ marginRight: '5px' }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteHoliday(holiday.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Holiday Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedHoliday(null);
          resetForm();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{showEditModal ? 'Edit Holiday' : 'Add Holiday'}</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedHoliday(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Holiday Name *</label>
                <input
                  type="text"
                  value={holidayForm.holiday_name}
                  onChange={(e) => setHolidayForm({ ...holidayForm, holiday_name: e.target.value })}
                  placeholder="e.g., New Year's Day"
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={holidayForm.holiday_date}
                  onChange={(e) => setHolidayForm({ ...holidayForm, holiday_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Applies To</label>
                <select
                  value={holidayForm.applies_to}
                  onChange={(e) => setHolidayForm({ ...holidayForm, applies_to: e.target.value, country_iso2: e.target.value !== 'country' ? '' : holidayForm.country_iso2 })}
                >
                  <option value="all">All Doctors</option>
                  <option value="country">Country</option>
                  <option value="practice_type">Practice Type</option>
                </select>
              </div>
              {holidayForm.applies_to === 'country' && (
                <div className="form-group">
                  <label>Country *</label>
                  <select
                    value={holidayForm.country_iso2}
                    onChange={(e) => setHolidayForm({ ...holidayForm, country_iso2: e.target.value })}
                    required
                  >
                    <option value="">Select a country</option>
                    {countryCodes.map(country => (
                      <option key={country.id} value={country.iso2}>
                        {country.country_name} ({country.iso2})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {holidayForm.applies_to === 'practice_type' && (
                <div className="form-group">
                  <label>Practice Type</label>
                  <input
                    type="text"
                    value={holidayForm.practice_type}
                    onChange={(e) => setHolidayForm({ ...holidayForm, practice_type: e.target.value })}
                    placeholder="e.g., clinic, hospital"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Year</label>
                <input
                  type="number"
                  value={holidayForm.year ?? ''}
                  onChange={(e) => {
                    const yearValue = e.target.value ? parseInt(e.target.value) : null;
                    setHolidayForm({ ...holidayForm, year: isNaN(yearValue as number) ? null : yearValue });
                  }}
                  placeholder="Leave empty for all years"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={holidayForm.is_recurring}
                    onChange={(e) => setHolidayForm({ ...holidayForm, is_recurring: e.target.checked })}
                  />
                  Is Recurring (Annual)
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={holidayForm.is_active}
                    onChange={(e) => setHolidayForm({ ...holidayForm, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={autoCancelAppointments}
                    onChange={(e) => setAutoCancelAppointments(e.target.checked)}
                  />
                  Auto-cancel appointments on this date
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedHoliday(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={showEditModal ? handleUpdateHoliday : handleCreateHoliday}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : (showEditModal ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="modal-overlay" onClick={() => {
          setShowBulkImportModal(false);
          setBulkHolidaysText('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Bulk Import Holidays</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowBulkImportModal(false);
                  setBulkHolidaysText('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>
                  Holidays (one per line, format: "Holiday Name,YYYY-MM-DD")
                </label>
                <textarea
                  value={bulkHolidaysText}
                  onChange={(e) => setBulkHolidaysText(e.target.value)}
                  placeholder="New Year's Day,2025-01-01&#10;Independence Day,2025-08-15&#10;Christmas,2025-12-25"
                  rows={10}
                  style={{ width: '100%', fontFamily: 'monospace' }}
                />
                <small style={{ color: '#666' }}>
                  Example: New Year's Day,2025-01-01
                </small>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={autoCancelAppointments}
                    onChange={(e) => setAutoCancelAppointments(e.target.checked)}
                  />
                  Auto-cancel appointments on affected dates
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={importOnlyWithoutAppointments}
                    onChange={(e) => setImportOnlyWithoutAppointments(e.target.checked)}
                  />
                  Import only dates without appointments
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handlePreCheckBulk}
                disabled={isLoading}
              >
                Pre-check
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkImportModal(false);
                  setBulkHolidaysText('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkImport}
                disabled={isLoading}
              >
                {isLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Year Import Modal */}
      {showYearImportModal && (
        <div className="modal-overlay" onClick={() => setShowYearImportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import Common Holidays for {selectedYear}</h3>
              <button
                className="modal-close"
                onClick={() => setShowYearImportModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>This will import common holidays for {selectedYear}:</p>
              <ul>
                <li>New Year's Day (Jan 1)</li>
                <li>Republic Day (Jan 26)</li>
                <li>Independence Day (Aug 15)</li>
                <li>Gandhi Jayanti (Oct 2)</li>
                <li>Christmas (Dec 25)</li>
              </ul>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={autoCancelAppointments}
                    onChange={(e) => setAutoCancelAppointments(e.target.checked)}
                  />
                  Auto-cancel appointments on affected dates
                </label>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={importOnlyWithoutAppointments}
                    onChange={(e) => setImportOnlyWithoutAppointments(e.target.checked)}
                  />
                  Import only dates without appointments
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handlePreCheckYear}
                disabled={isLoading}
              >
                Pre-check
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowYearImportModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImportYear}
                disabled={isLoading}
              >
                {isLoading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-check Summary Modal */}
      {showPreCheckModal && preCheckSummary && (
        <div className="modal-overlay" onClick={() => {
          setShowPreCheckModal(false);
          setPreCheckSummary(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>Bulk Import Pre-check Summary</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowPreCheckModal(false);
                  setPreCheckSummary(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '20px' }}>
                <strong>Total Holidays:</strong> {preCheckSummary.total_holidays}
              </div>
              {preCheckSummary.dates_with_appointments > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '5px' }}>
                  <strong style={{ color: '#856404' }}>⚠️ Conflicts Detected</strong>
                  <p style={{ margin: '10px 0 0 0', color: '#856404' }}>
                    {preCheckSummary.dates_with_appointments} date(s) have existing appointments
                    ({preCheckSummary.affected_appointments_count} appointment(s) total)
                  </p>
                  {preCheckSummary.dates_with_appointments_list.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <strong>Dates with appointments:</strong>
                      <ul style={{ marginTop: '10px' }}>
                        {preCheckSummary.dates_with_appointments_list.map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '10px' }}>
                            <strong>{item.holiday_name}</strong> ({item.holiday_date}): {item.appointment_count} appointment(s)
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {preCheckSummary.holidays_to_import.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <strong>Holidays to import ({preCheckSummary.holidays_to_import.length}):</strong>
                  <ul style={{ marginTop: '10px' }}>
                    {preCheckSummary.holidays_to_import.map((holiday, idx) => (
                      <li key={idx}>
                        {holiday.holiday_name} - {holiday.holiday_date}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {preCheckSummary.holidays_skipped && preCheckSummary.holidays_skipped.length > 0 && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                  <strong>Skipped Holidays ({preCheckSummary.holidays_skipped.length}):</strong>
                  <ul style={{ marginTop: '10px' }}>
                    {preCheckSummary.holidays_skipped.map((holiday: any, idx: number) => (
                      <li key={idx}>
                        {holiday.holiday_name || holiday.holiday_date} - {holiday.reason || 'Has appointments'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPreCheckModal(false);
                  setPreCheckSummary(null);
                }}
              >
                Close
              </button>
              {showBulkImportModal && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowPreCheckModal(false);
                    handleBulkImport();
                  }}
                  disabled={isLoading}
                >
                  Proceed with Import
                </button>
              )}
              {showYearImportModal && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowPreCheckModal(false);
                    handleImportYear();
                  }}
                  disabled={isLoading}
                >
                  Proceed with Import
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteConfirm(false);
          setDeleteHolidayId(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteHolidayId(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this holiday? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteHolidayId(null);
                }}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDeleteHoliday}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHolidays;

